import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Voice-to-text button using the browser's Web Speech API (webkitSpeechRecognition).
 * Appends recognized speech to the current textarea value.
 * Works in Chrome/Edge; gracefully hides on unsupported browsers.
 */
const VoiceInput = ({ value, onChange, testId = 'voice-input-btn' }) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const valueRef = useRef(value);

  useEffect(() => { valueRef.current = value; }, [value]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = navigator.language || 'en-US';
    rec.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) transcript += event.results[i][0].transcript + ' ';
      }
      if (transcript) {
        const current = valueRef.current || '';
        const glue = current && !current.endsWith(' ') ? ' ' : '';
        onChange(current + glue + transcript.trim());
      }
    };
    rec.onerror = (e) => {
      if (e.error !== 'aborted') toast.error(`Voice input: ${e.error}`);
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    return () => { try { rec.abort(); } catch { /* noop */ } };
  }, []);

  const supported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!supported) return null;

  const toggle = () => {
    if (!recognitionRef.current) return;
    if (listening) {
      recognitionRef.current.stop();
      setListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setListening(true);
      } catch {
        setListening(false);
      }
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className={`voice-input-btn ${listening ? 'listening' : ''}`}
      data-testid={testId}
      title={listening ? 'Stop dictation' : 'Dictate'}
    >
      <i className={`fa-solid ${listening ? 'fa-stop' : 'fa-microphone'}`} />
      {listening ? 'Stop' : 'Dictate'}
    </button>
  );
};

export default VoiceInput;
